// src/helpers/axios.ts
import axios from "axios";
import { usePwaStore } from "@/stores/pwa";

const axiosInstance = axios.create();

axiosInstance.defaults.withCredentials = true;

const token = decodeURIComponent(document.cookie.split("; ").find(row => row.startsWith("wca_session="))?.split("=")[1] || "");
axiosInstance.defaults.headers.common["wca_session"] = token;

let lastShowedAxiosError = new Date();

const isCypress =
	typeof window !== "undefined" &&
	(window as any).Cypress !== undefined ||
	process.env.VITE_IS_CYPRESS === "true";

const SLOW_REQUEST_TIMEOUT = 10 * 1000;

const TIMEOUTS: [RegExp, number][] = [
	[/^\/api\/user/, 10 * 1000],
	[/^\/api\/device\/check/, 10 * 1000]
];
const abortAnywayExceptions = [
	"/sanctum/csrf-cookie",
	"/api/users/auth",
	"undefined"
];
const neverAbortExceptions = [
	"/logout",
	"/login",
	"/forgot-password"
];

const getTimeoutForUrl = (url?: string): number => {
	if (!url) return 10 * 1000;
	for (const [pattern, timeout] of TIMEOUTS) {
		if (pattern.test(url)) return timeout;
	}
	return SLOW_REQUEST_TIMEOUT;
};

// ⛔ Intercept all outgoing requests
axiosInstance.interceptors.request.use((config) => {
	const shouldSkipAbort = neverAbortExceptions.some(substring =>
		config.url?.includes(substring)
	);
	if (shouldSkipAbort)
		return config;
	if (isCypress) {
		console.log("[Axios] Cypress mode: skipping abort logic");
		return config;
	}

	const pwaStore = usePwaStore();
	const controller = new AbortController();
	if (pwaStore.manuallyOnline === false) {
		// Immediately abort the request if the user is working offline,
		// with the exception of the logout route
		controller.abort();
		pwaStore.endUpdate(null);

		config.signal = controller.signal;

		// Store the timeout ID so it can be cleared later
		(config as any).metadata = { manuallyOnline: pwaStore.manuallyOnline };

		console.warn("❌ Request aborted because you are offline");
		// return config;
	}
	else {

		config.signal = controller.signal;

		// 👇 Match timeout based on route or use default
		const timeout = getTimeoutForUrl(config.url);

		// const timeoutId = setTimeout(() => {
		// 	if (confirm("requests taking a long time. continue to wait?"))
		// 		clearTimeout(timeoutId);
		// 	else
		// 		controller.abort();
		// }, timeout);

		// Store the timeout ID so it can be cleared later
		// (config as any).metadata = { timeoutId: timeoutId, maxtime: timeout, manuallyOnline: pwaStore.manuallyOnline };

		// Attach metadata for tracking
		(config as any).metadata = {
			maxtime: timeout,
			manuallyOnline: pwaStore.manuallyOnline,
			controller,
			promptTimeout: setTimeout(async () => {
				const now = new Date();
				// console.log((now.getTime() - lastShowedAxiosError.getTime())/1000);
				if (abortAnywayExceptions.includes(config.url || "undefined"))
					// these routes shouldnt ask user permission to abort
					controller.abort();
				else {
					// if the last time this modal was shown was more than 15s ago
					if ((now.getTime() - lastShowedAxiosError.getTime()) > 15000) {
						const confirmed = window.confirm(
							"Updates to the server are taking longer than usual. Would you like to set the app to offline mode?\r\rYou can turn the app online again once data or wifi signal improves to sync your data."
						);
						lastShowedAxiosError = now;
						if (confirmed) {
							pwaStore.setManuallyOnline(false);
							pwaStore.endUpdate(null);
							controller.abort();
						}
					}
				}

			}, timeout)
		};
	}

	return config;
}, (error) => Promise.reject(error));

axiosInstance.interceptors.response.use(
	(response) => {
		// clearTimeout((response.config as any).metadata?.timeoutId);

		const metadata = (response.config as any)?.metadata;
		if (metadata?.promptTimeout) {
			clearTimeout(metadata.promptTimeout);
		}
		return response;
	},
	(error) => {
		const pwaStore = usePwaStore();
		// clearTimeout((error.config as any)?.metadata?.timeoutId);
		const config = error.config || {};
		const metadata = (config as any).metadata;
		if (metadata?.promptTimeout) {
			clearTimeout(metadata.promptTimeout);
		}

		if (error.code === "ERR_CANCELED" || error.name === "CanceledError") {
			// check if we were offline when the request was sent (in which case it aborted)
			if ((error.config as any)?.metadata?.manuallyOnline === false) {
				console.warn("❌ Response received an aborted request due to offline mode");
				// alertStore.warning?.("You are offline. Request cancelled.");
			} else {
				const L = (error.config as any)?.metadata?.maxtime;
				console.warn(`🚫 Request aborted after ${L}ms:`, error.config?.url);
			}
		}

		return Promise.reject(error);
	});

export default axiosInstance;
